/* ─────────────────────────────────────────────
   Loop Lab — Pre-order
   Keeps pre-order and in-stock items out of the same cart.

   Shopify allows exactly one cart per session, so rather than inventing a
   second cart we keep the single cart homogeneous: it holds either pre-order
   lines or in-stock lines, never both. When a shopper tries to mix them we
   offer to hold the current cart aside (localStorage) and hand it back after
   they have checked out.

   A pre-order line is identified by the line item property "Pre-order", which
   is attached at add time and also shows on the order and packing slip.

   This file decorates LoopCart.addItem rather than editing theme.js, so the
   whole feature can be removed by dropping the two tags in layout/theme.liquid.
   ───────────────────────────────────────────── */
(function () {
  'use strict';

  var PROP_KEY = 'Pre-order';
  var STASH_KEY = 'looplab:cart-stash';
  var PILL_DISMISS_KEY = 'looplab:stash-pill-dismissed';

  /* ---------- pre-order variant lookup ---------- */

  /* Built from the data-* markers that preorder-badge / preorder-pdp emit. */
  function variantMap() {
    var map = {};
    var nodes = document.querySelectorAll('[data-preorder-variant]');
    for (var i = 0; i < nodes.length; i++) {
      var id = String(nodes[i].getAttribute('data-preorder-variant') || '').trim();
      if (!id) continue;
      map[id] = (nodes[i].getAttribute('data-preorder-ship') || '').trim();
    }
    return map;
  }

  function isPreorderVariant(variantId) {
    return Object.prototype.hasOwnProperty.call(variantMap(), String(variantId));
  }

  function propsFor(variantId) {
    var map = variantMap();
    var key = String(variantId);
    if (!Object.prototype.hasOwnProperty.call(map, key)) return null;
    var props = {};
    props[PROP_KEY] = map[key] ? 'Ships by ' + map[key] : 'Yes';
    return props;
  }

  function isPreorderLine(item) {
    return !!(item && item.properties && item.properties[PROP_KEY]);
  }

  /* ---------- cart helpers ---------- */

  function getCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' })
      .then(function (r) { return r.json(); });
  }

  function clearCart() {
    return fetch('/cart/clear.js', { method: 'POST', headers: { Accept: 'application/json' } });
  }

  function postAdd(items) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items })
    }).then(function (res) {
      if (res.ok) return res.json();
      return res.json().catch(function () { return {}; }).then(function (d) {
        throw new Error(d.description || d.message || 'Could not add to cart');
      });
    });
  }

  /* ---------- stash ---------- */

  function readStash() {
    try {
      var raw = localStorage.getItem(STASH_KEY);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      return parsed && parsed.items && parsed.items.length ? parsed : null;
    } catch (e) { return null; }
  }

  function writeStash(kind, items) {
    try {
      localStorage.setItem(STASH_KEY, JSON.stringify({ kind: kind, savedAt: Date.now(), items: items }));
      localStorage.removeItem(PILL_DISMISS_KEY);
    } catch (e) { /* storage unavailable — the hold simply won't persist */ }
  }

  function dropStash() {
    try { localStorage.removeItem(STASH_KEY); } catch (e) {}
  }

  function stashFromCart(cart) {
    return cart.items.map(function (item) {
      var entry = { id: item.variant_id, quantity: item.quantity };
      /* Keep the shopper's own line properties, not Shopify's internal _ ones. */
      var props = {};
      var has = false;
      for (var k in (item.properties || {})) {
        if (k.charAt(0) === '_') continue;
        props[k] = item.properties[k];
        has = true;
      }
      if (has) entry.properties = props;
      return entry;
    });
  }

  /* ---------- modal ---------- */

  var modalEl = null;

  function buildModal() {
    if (modalEl) return modalEl;
    modalEl = document.createElement('div');
    modalEl.className = 'preorder-modal';
    modalEl.setAttribute('role', 'dialog');
    modalEl.setAttribute('aria-modal', 'true');
    modalEl.hidden = true;
    modalEl.innerHTML =
      '<div class="preorder-modal__scrim" data-po-cancel></div>' +
      '<div class="preorder-modal__box" role="document">' +
        '<h2 class="preorder-modal__title" data-po-title></h2>' +
        '<p class="preorder-modal__body" data-po-body></p>' +
        '<div class="preorder-modal__actions">' +
          '<button type="button" class="preorder-modal__btn preorder-modal__btn--primary" data-po-confirm></button>' +
          '<button type="button" class="preorder-modal__btn preorder-modal__btn--ghost" data-po-cancel></button>' +
        '</div>' +
      '</div>';
    document.body.appendChild(modalEl);
    return modalEl;
  }

  function ask(opts) {
    return new Promise(function (resolve) {
      var el = buildModal();
      el.querySelector('[data-po-title]').textContent = opts.title;
      el.querySelector('[data-po-body]').textContent = opts.body;
      var confirmBtn = el.querySelector('[data-po-confirm]');
      var cancelBtns = el.querySelectorAll('[data-po-cancel]');
      confirmBtn.textContent = opts.confirm;
      el.querySelector('.preorder-modal__btn--ghost').textContent = opts.cancel;

      function done(result) {
        el.hidden = true;
        el.classList.remove('is-open');
        document.body.style.overflow = '';
        document.removeEventListener('keydown', onKey);
        confirmBtn.removeEventListener('click', onYes);
        for (var i = 0; i < cancelBtns.length; i++) cancelBtns[i].removeEventListener('click', onNo);
        resolve(result);
      }
      function onYes() { done(true); }
      function onNo() { done(false); }
      function onKey(e) { if (e.key === 'Escape') done(false); }

      confirmBtn.addEventListener('click', onYes);
      for (var i = 0; i < cancelBtns.length; i++) cancelBtns[i].addEventListener('click', onNo);
      document.addEventListener('keydown', onKey);

      el.hidden = false;
      document.body.style.overflow = 'hidden';
      /* rAF gives the transition a frame to start from, but browsers pause rAF
         in background tabs — without the timer fallback the modal could sit
         invisible behind a scroll-locked page. */
      var opened = false;
      function paint() {
        if (opened) return;
        opened = true;
        el.classList.add('is-open');
      }
      requestAnimationFrame(paint);
      setTimeout(paint, 50);
      confirmBtn.focus();
    });
  }

  /* ---------- the guard ---------- */

  /* Resolves true when it is safe to add, false when the shopper backed out. */
  function ensureCompatible(addingPreorder) {
    return getCart().then(function (cart) {
      var items = cart.items || [];
      if (!items.length) return true;

      var hasPre = items.some(isPreorderLine);
      var hasReg = items.some(function (i) { return !isPreorderLine(i); });

      var conflict = (addingPreorder && hasReg) || (!addingPreorder && hasPre);
      if (!conflict) return true;

      var n = items.length;
      var noun = n === 1 ? 'item' : 'items';
      var opts = addingPreorder
        ? {
            title: 'Pre-orders check out separately',
            body: 'Your cart has ' + n + ' in-stock ' + noun + '. Keeping them apart means your in-stock order ships straight away instead of waiting on the pre-order. We can hold your current cart and give it back after you check out.',
            confirm: 'Hold my cart & pre-order',
            cancel: 'Keep my cart'
          }
        : {
            title: 'You have a pre-order in your cart',
            body: 'Your cart holds ' + n + ' pre-order ' + noun + '. Adding in-stock items would make them wait for the pre-order to arrive. We can hold the pre-order and give it back after you check out.',
            confirm: 'Hold pre-order & continue',
            cancel: 'Keep my cart'
          };

      return ask(opts).then(function (yes) {
        if (!yes) return false;
        writeStash(hasPre ? 'preorder' : 'regular', stashFromCart(cart));
        return clearCart().then(function () {
          if (typeof LoopCart !== 'undefined' && LoopCart.updateCountBadge) LoopCart.updateCountBadge(0);
          return true;
        });
      });
    }).catch(function () {
      /* If the cart can't be read, don't block the sale. */
      return true;
    });
  }

  /* ---------- restore pill ---------- */

  function renderPill() {
    var existing = document.querySelector('.preorder-stash-pill');
    if (existing) existing.remove();

    var stash = readStash();
    if (!stash) return;
    try { if (localStorage.getItem(PILL_DISMISS_KEY)) return; } catch (e) {}

    var n = stash.items.reduce(function (a, i) { return a + (i.quantity || 1); }, 0);
    var label = stash.kind === 'preorder' ? 'pre-order' : 'saved';

    var pill = document.createElement('div');
    pill.className = 'preorder-stash-pill';
    pill.innerHTML =
      '<span class="preorder-stash-pill__text">' + n + ' ' + label + ' item' + (n === 1 ? '' : 's') + ' held</span>' +
      '<button type="button" class="preorder-stash-pill__restore">Restore</button>' +
      '<button type="button" class="preorder-stash-pill__close" aria-label="Dismiss">&times;</button>';
    document.body.appendChild(pill);

    pill.querySelector('.preorder-stash-pill__close').addEventListener('click', function () {
      try { localStorage.setItem(PILL_DISMISS_KEY, '1'); } catch (e) {}
      pill.remove();
    });

    pill.querySelector('.preorder-stash-pill__restore').addEventListener('click', function () {
      var btn = this;
      btn.disabled = true;
      btn.textContent = 'Restoring…';
      getCart().then(function (cart) {
        var items = cart.items || [];
        if (items.length) {
          var cartIsPre = items.some(isPreorderLine);
          var stashIsPre = stash.kind === 'preorder';
          if (cartIsPre !== stashIsPre) {
            return ask({
              title: 'Check out your cart first',
              body: 'Your held items and the items in your cart can\'t travel together. Check out what\'s in your cart, then restore the held items.',
              confirm: 'Open my cart',
              cancel: 'Not now'
            }).then(function (openCart) {
              btn.disabled = false;
              btn.textContent = 'Restore';
              if (openCart) document.dispatchEvent(new Event('cart:open'));
              return null;
            });
          }
        }
        return postAdd(stash.items).then(function () {
          dropStash();
          pill.remove();
          if (typeof LoopCart !== 'undefined' && LoopCart.refreshCount) return LoopCart.refreshCount();
        }).then(function () {
          document.dispatchEvent(new Event('cart:open'));
        });
      }).catch(function (err) {
        console.error('Pre-order restore failed', err);
        btn.disabled = false;
        btn.textContent = 'Restore';
      });
    });
  }

  /* ---------- wire up ---------- */

  function init() {
    if (typeof LoopCart === 'undefined' || !LoopCart || typeof LoopCart.addItem !== 'function') return;

    LoopCart.addItem = function (variantId, quantity, extraProps) {
      var qty = quantity || 1;
      var auto = propsFor(variantId);
      var addingPreorder = !!auto;
      var props = Object.assign({}, auto, extraProps || null);

      return ensureCompatible(addingPreorder).then(function (ok) {
        if (!ok) {
          var err = new Error('Add cancelled by shopper');
          err.code = 'PREORDER_CANCELLED';
          throw err;
        }
        var line = { id: variantId, quantity: qty };
        if (Object.keys(props).length) line.properties = props;
        return postAdd([line]);
      }).then(function (result) {
        renderPill();
        return result;
      });
    };

    /* For callers that build their own /cart/add.js request (the bundle page). */
    window.LoopPreorder = {
      propertyKey: PROP_KEY,
      isPreorderVariant: isPreorderVariant,
      propsFor: propsFor,
      isPreorderLine: isPreorderLine,
      check: ensureCompatible,
      refreshPill: renderPill
    };

    renderPill();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
